import { useState } from "react";
import {
  UploadSimple, Plus, Trash, ArrowUp, ArrowDown, ChatCenteredText, TextT,
  CheckSquare, Textbox, ListChecks, Sliders, SquaresFour, CircleNotch, CheckCircle, XCircle, Clock,
} from "@phosphor-icons/react";

const inputCls = "w-full bg-[#050505] border border-[#27272A] px-3 py-2 text-sm font-mono focus:border-[#00F0FF] focus:outline-none transition-colors duration-200";
const labelCls = "block font-mono text-[10px] text-[#71717A] mb-1.5 uppercase tracking-wider";

const Field = ({ label, children }) => (<div><label className={labelCls}>{label}</label>{children}</div>);
const Txt = ({ k, values, setVal, ph, num }) => (
  <input data-testid={`sd-${k}`} type={num ? "number" : "text"} placeholder={ph} value={values[k] ?? ""} onChange={(e) => setVal(k, e.target.value)} className={inputCls} />
);
const Toggle = ({ k, values, setVal }) => (
  <button type="button" data-testid={`sd-${k}`} onClick={() => setVal(k, !values[k])}
    className={`flex items-center gap-2 px-3 py-2 border text-xs transition-colors duration-200 ${values[k] ? "border-[#00FF41] text-[#00FF41] bg-[#00FF41]/5" : "border-[#27272A] text-[#71717A]"}`}>
    <span className={`w-8 h-4 rounded-full relative transition-colors ${values[k] ? "bg-[#00FF41]/40" : "bg-[#27272A]"}`}>
      <span className={`absolute top-0.5 w-3 h-3 rounded-full transition-all ${values[k] ? "left-4 bg-[#00FF41]" : "left-0.5 bg-[#71717A]"}`} />
    </span>{values[k] ? "On" : "Off"}
  </button>
);
const Sel = ({ k, values, setVal, options }) => (
  <select data-testid={`sd-${k}`} value={values[k] ?? options[0]} onChange={(e) => setVal(k, e.target.value)} className={inputCls}>
    {options.map((o) => <option key={o} value={o}>{o || "(none)"}</option>)}
  </select>
);

function ListEditor({ k, values, setVal, cols, testid }) {
  const items = Array.isArray(values[k]) ? values[k] : [];
  const set = (next) => setVal(k, next);
  const add = () => { const b = {}; cols.forEach((c) => (b[c.key] = c.type === "bool" ? false : "")); set([...items, b]); };
  const upd = (i, key, v) => set(items.map((it, idx) => (idx === i ? { ...it, [key]: v } : it)));
  const rm = (i) => set(items.filter((_, idx) => idx !== i));
  const mv = (i, d) => { const to = i + d; if (to < 0 || to >= items.length) return; const n = [...items]; const [m] = n.splice(i, 1); n.splice(to, 0, m); set(n); };
  return (
    <div className="space-y-2" data-testid={testid}>
      {items.map((it, i) => (
        <div key={i} data-testid={`${testid}-item-${i}`} className="border border-[#27272A] bg-[#0f0f11] p-2.5 space-y-2">
          <div className="flex items-center justify-between">
            <span className="font-mono text-[9px] uppercase tracking-wider text-[#71717A]">#{i + 1}</span>
            <div className="flex items-center gap-1.5">
              <button data-testid={`${testid}-up-${i}`} disabled={i === 0} onClick={() => mv(i, -1)} className="text-[#71717A] hover:text-[#00F0FF] disabled:opacity-20"><ArrowUp size={13} /></button>
              <button data-testid={`${testid}-down-${i}`} disabled={i === items.length - 1} onClick={() => mv(i, 1)} className="text-[#71717A] hover:text-[#00F0FF] disabled:opacity-20"><ArrowDown size={13} /></button>
              <button data-testid={`${testid}-remove-${i}`} onClick={() => rm(i)} className="text-[#71717A] hover:text-[#FF5F56]"><Trash size={13} /></button>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            {cols.map((c) => (
              <div key={c.key} className={c.full ? "col-span-2" : ""}>
                <label className="block font-mono text-[9px] text-[#52525B] mb-1 uppercase tracking-wider">{c.label}</label>
                {c.type === "bool" ? (
                  <button data-testid={`${testid}-${i}-${c.key}`} onClick={() => upd(i, c.key, !it[c.key])}
                    className={`px-2 py-1.5 border text-[11px] w-full ${it[c.key] ? "border-[#00FF41] text-[#00FF41]" : "border-[#27272A] text-[#71717A]"}`}>{it[c.key] ? "Yes" : "No"}</button>
                ) : c.type === "select" ? (
                  <select data-testid={`${testid}-${i}-${c.key}`} value={it[c.key] ?? ""} onChange={(e) => upd(i, c.key, e.target.value)} className={inputCls}>
                    {c.options.map((o) => <option key={o} value={o}>{o || "(none)"}</option>)}
                  </select>
                ) : (
                  <input data-testid={`${testid}-${i}-${c.key}`} value={it[c.key] ?? ""} onChange={(e) => upd(i, c.key, e.target.value)} className={inputCls} />
                )}
              </div>
            ))}
          </div>
        </div>
      ))}
      <button data-testid={`${testid}-add`} onClick={add} className="flex items-center gap-1.5 border border-[#27272A] text-[#A1A1AA] px-3 py-1.5 text-xs hover:border-[#00F0FF] hover:text-[#00F0FF] transition-colors"><Plus size={13} /> Add</button>
    </div>
  );
}

const TABS = [
  { id: "content", label: "Content", icon: ChatCenteredText },
  { id: "buttons", label: "Buttons", icon: SquaresFour },
  { id: "behavior", label: "Behavior", icon: Sliders },
  { id: "elements", label: "Elements", icon: ListChecks },
];

export function SwiftDialogEditor({ values, setVal, handleUpload }) {
  const [tab, setTab] = useState("content");
  const P = { values, setVal };
  const uploadField = (k, label) => (
    <Field label={label}>
      <div className="flex gap-2">
        <input data-testid={`sd-${k}`} value={values[k] ?? ""} onChange={(e) => setVal(k, e.target.value)} className={inputCls} placeholder="URL or upload" />
        <label className="flex items-center gap-1.5 border border-[#27272A] px-3 text-xs text-[#A1A1AA] hover:border-[#00F0FF] hover:text-[#00F0FF] cursor-pointer whitespace-nowrap">
          <UploadSimple size={14} /> Upload
          <input type="file" accept="image/*" className="hidden" data-testid={`sd-upload-${k}`} onChange={(e) => handleUpload(k, e)} />
        </label>
      </div>
    </Field>
  );
  return (
    <div data-testid="sd-editor">
      <div className="flex flex-wrap gap-1 mb-5 border-b border-[#27272A]">
        {TABS.map((t) => (
          <button key={t.id} data-testid={`sd-tab-${t.id}`} onClick={() => setTab(t.id)}
            className={`flex items-center gap-1.5 px-3 py-2 text-xs font-mono border-b-2 -mb-px transition-colors ${tab === t.id ? "border-[#00F0FF] text-[#00F0FF]" : "border-transparent text-[#71717A] hover:text-[#A1A1AA]"}`}>
            <t.icon size={13} /> {t.label}
          </button>
        ))}
      </div>

      {tab === "content" && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Title"><Txt k="title" {...P} /></Field>
          <Field label="Title font size (0 = default)"><Txt k="titlefont_size" num {...P} /></Field>
          <Field label="Title color (hex, optional)"><Txt k="title_color" {...P} /></Field>
          <Field label="Message alignment"><Sel k="message_alignment" {...P} options={["left", "center", "right"]} /></Field>
          <div className="md:col-span-2"><Field label="Message (markdown)"><textarea data-testid="sd-message" value={values.message ?? ""} onChange={(e) => setVal("message", e.target.value)} className={inputCls + " min-h-[70px]"} /></Field></div>
          <Field label="Icon (SF=name / URL / app path)"><Txt k="icon" {...P} /></Field>
          <Field label="Icon size (0 = default)"><Txt k="icon_size" num {...P} /></Field>
          <Field label="Overlay icon (badge, optional)"><Txt k="overlayicon" {...P} /></Field>
          {uploadField("image", "Hero image (optional)")}
          <Field label="Image caption"><Txt k="image_caption" {...P} /></Field>
          {uploadField("banner_image", "Banner image (optional)")}
          <Field label="Banner title"><Txt k="banner_title" {...P} /></Field>
        </div>
      )}
      {tab === "buttons" && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Primary button text"><Txt k="button1" {...P} /></Field>
          <Field label="Primary button action (URL)"><Txt k="button1action" {...P} /></Field>
          <Field label="Secondary button text"><Txt k="button2" {...P} /></Field>
          <Field label="Help message (? bubble)"><Txt k="helpmessage" {...P} /></Field>
          <Field label="Info button text"><Txt k="infobutton_text" {...P} /></Field>
          <Field label="Info button URL"><Txt k="infobutton_action" {...P} /></Field>
        </div>
      )}
      {tab === "behavior" && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Window position"><Sel k="position" {...P} options={["center", "top", "bottom", "left", "right", "topleft", "topright", "bottomleft", "bottomright"]} /></Field>
          <Field label="Size preset"><Sel k="size" {...P} options={["default", "mini", "small", "big", "fullscreen"]} /></Field>
          <Field label="Custom width (0 = auto)"><Txt k="width" num {...P} /></Field>
          <Field label="Custom height (0 = auto)"><Txt k="height" num {...P} /></Field>
          <Field label="Auto-dismiss timer (s, 0 = off)"><Txt k="timer" num {...P} /></Field>
          <Field label="Hide timer bar"><Toggle k="hidetimerbar" {...P} /></Field>
          <Field label="Progress bar steps (0 = off)"><Txt k="progress" num {...P} /></Field>
          <Field label="Progress text"><Txt k="progresstext" {...P} /></Field>
          <Field label="Quit key (single char)"><Txt k="quitkey" {...P} /></Field>
          <Field label="Moveable window"><Toggle k="moveable" {...P} /></Field>
          <Field label="Blur screen behind"><Toggle k="blurscreen" {...P} /></Field>
          <Field label="Keep on top"><Toggle k="ontop" {...P} /></Field>
        </div>
      )}
      {tab === "elements" && (
        <div className="space-y-5">
          <div><label className={labelCls + " flex items-center gap-1.5"}><ListChecks size={12} /> List items (progress list)</label>
            <ListEditor k="listitems" testid="sd-listitems" {...P} cols={[
              { key: "title", label: "Title", full: true }, { key: "icon", label: "Icon" }, { key: "statustext", label: "Status text" },
              { key: "status", label: "Status", type: "select", options: ["", "pending", "wait", "progress", "success", "fail", "error"] },
            ]} /></div>
          <div><label className={labelCls + " flex items-center gap-1.5"}><CheckSquare size={12} /> Checkboxes</label>
            <ListEditor k="checkboxes" testid="sd-checkboxes" {...P} cols={[
              { key: "label", label: "Label", full: true }, { key: "checked", label: "Checked", type: "bool" }, { key: "disabled", label: "Disabled", type: "bool" },
            ]} /></div>
          <div><label className={labelCls + " flex items-center gap-1.5"}><Textbox size={12} /> Text fields</label>
            <ListEditor k="textfields" testid="sd-textfields" {...P} cols={[
              { key: "title", label: "Label" }, { key: "prompt", label: "Placeholder" }, { key: "value", label: "Default value" },
              { key: "regex", label: "Regex" }, { key: "secure", label: "Secure", type: "bool" }, { key: "required", label: "Required", type: "bool" },
            ]} /></div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Field label="Dropdown label"><Txt k="dropdown_title" {...P} /></Field>
            <Field label="Options (comma-sep)"><Txt k="dropdown_values" {...P} /></Field>
            <Field label="Default value"><Txt k="dropdown_default" {...P} /></Field>
          </div>
        </div>
      )}
    </div>
  );
}

const STATUS_ICON = { success: CheckCircle, fail: XCircle, error: XCircle, wait: CircleNotch, progress: CircleNotch, pending: Clock };
const STATUS_COLOR = { success: "#30D158", fail: "#FF453A", error: "#FF453A", wait: "#0A84FF", progress: "#0A84FF", pending: "#8E8E93" };

export function SwiftDialogPreview({ values }) {
  const list = (Array.isArray(values.listitems) ? values.listitems : []).filter((i) => (i.title || "").trim());
  const checks = (Array.isArray(values.checkboxes) ? values.checkboxes : []).filter((c) => (c.label || "").trim());
  const fields = (Array.isArray(values.textfields) ? values.textfields : []).filter((t) => (t.title || "").trim());
  const dvals = String(values.dropdown_values || "").split(",").map((s) => s.trim()).filter(Boolean);
  const iconTxt = String(values.icon || "");
  const iconIsImg = iconTxt.startsWith("http");
  return (
    <div data-testid="sd-preview">
      <div className="rounded-2xl p-3 bg-gradient-to-br from-[#1b2735] to-[#0b1220]">
        <div className="rounded-xl bg-[#ECECEC] shadow-2xl overflow-hidden text-[#1d1d1f]">
          {(values.banner_image || values.banner_title) && (
            <div className="h-14 bg-[#0A84FF] flex items-center px-4 relative overflow-hidden">
              {values.banner_image && values.banner_image.startsWith("http") && <img src={values.banner_image} alt="" className="absolute inset-0 w-full h-full object-cover opacity-70" onError={(e) => (e.currentTarget.style.display = "none")} />}
              <span className="relative text-white font-semibold text-sm">{values.banner_title}</span>
            </div>
          )}
          <div className="p-5">
            <div className="flex items-start gap-4">
              <div className="w-14 h-14 rounded-2xl bg-[#0A84FF]/10 border border-[#0A84FF]/30 flex items-center justify-center shrink-0 overflow-hidden">
                {iconIsImg ? <img src={iconTxt} alt="" className="w-full h-full object-contain" onError={(e) => (e.currentTarget.style.display = "none")} /> : <ChatCenteredText size={26} className="text-[#0A84FF]" />}
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-[17px] font-semibold leading-tight" style={values.title_color ? { color: values.title_color } : {}}>{values.title || "Dialog"}</div>
                <div className="text-[12px] text-[#3a3a3c] mt-1 leading-snug whitespace-pre-wrap" style={{ textAlign: values.message_alignment || "left" }}>{values.message}</div>
              </div>
            </div>

            {values.image && values.image.startsWith("http") && (
              <img src={values.image} alt="" className="w-full rounded-lg mt-4 max-h-32 object-cover" onError={(e) => (e.currentTarget.style.display = "none")} />
            )}

            {list.length > 0 && (
              <div className="mt-4 space-y-1.5">
                {list.map((it, i) => {
                  const SI = STATUS_ICON[it.status] || null;
                  return (
                    <div key={i} className="flex items-center gap-2.5 bg-white rounded-lg px-3 py-2 border border-black/5">
                      <span className="w-6 h-6 rounded-md bg-[#0A84FF]/10 flex items-center justify-center shrink-0"><SquaresFour size={13} className="text-[#0A84FF]" /></span>
                      <span className="text-[12px] flex-1 truncate">{it.title}</span>
                      {it.statustext && <span className="text-[10px] text-[#8e8e93]">{it.statustext}</span>}
                      {SI && <SI size={15} weight="fill" style={{ color: STATUS_COLOR[it.status] }} className={it.status === "wait" || it.status === "progress" ? "animate-spin" : ""} />}
                    </div>
                  );
                })}
              </div>
            )}

            {checks.length > 0 && (
              <div className="mt-4 space-y-1.5">
                {checks.map((c, i) => (
                  <label key={i} className="flex items-center gap-2 text-[12px]">
                    <span className={`w-4 h-4 rounded border flex items-center justify-center ${c.checked ? "bg-[#0A84FF] border-[#0A84FF]" : "border-[#c7c7cc] bg-white"} ${c.disabled ? "opacity-40" : ""}`}>{c.checked && <CheckCircle size={12} weight="fill" className="text-white" />}</span>
                    <span className={c.disabled ? "opacity-40" : ""}>{c.label}</span>
                  </label>
                ))}
              </div>
            )}

            {fields.map((t, i) => (
              <div key={i} className="mt-3">
                <div className="text-[11px] text-[#3a3a3c] mb-1">{t.title}{t.required ? " *" : ""}</div>
                <div className="bg-white border border-[#c7c7cc] rounded-md px-3 py-1.5 text-[12px] text-[#8e8e93]">{t.secure ? "••••••••" : (t.value || t.prompt || "")}</div>
              </div>
            ))}

            {values.dropdown_title && dvals.length > 0 && (
              <div className="mt-3">
                <div className="text-[11px] text-[#3a3a3c] mb-1">{values.dropdown_title}</div>
                <div className="bg-white border border-[#c7c7cc] rounded-md px-3 py-1.5 text-[12px] flex items-center justify-between"><span>{values.dropdown_default || dvals[0]}</span><span className="text-[#8e8e93]">▾</span></div>
              </div>
            )}

            {Number(values.progress) > 0 && (
              <div className="mt-4">
                <div className="h-1.5 rounded-full bg-black/10 overflow-hidden"><div className="h-full bg-[#0A84FF]" style={{ width: "30%" }} /></div>
                {values.progresstext && <div className="text-[10px] text-[#8e8e93] mt-1">{values.progresstext}</div>}
              </div>
            )}

            <div className="flex items-center gap-2 mt-5">
              {values.infobutton_text && <span className="text-[12px] text-[#0A84FF] border border-[#0A84FF]/40 rounded-md px-3 py-1.5">{values.infobutton_text}</span>}
              <div className="ml-auto flex items-center gap-2">
                {values.button2 && <span className="text-[12px] border border-[#c7c7cc] rounded-md px-4 py-1.5 bg-white">{values.button2}</span>}
                <span className="text-[12px] text-white bg-[#0A84FF] rounded-md px-4 py-1.5">{values.button1 || "OK"}</span>
              </div>
            </div>
            {Number(values.timer) > 0 && !values.hidetimerbar && (
              <div className="mt-3 h-1 rounded-full bg-[#0A84FF]/30 overflow-hidden"><div className="h-full bg-[#0A84FF] w-2/3" /></div>
            )}
          </div>
        </div>
      </div>
      <p className="text-[10px] text-[#71717A] font-mono mt-3 leading-relaxed">
        Live approximation of the swiftDialog window. Build generates a full JSON config + an install-and-run deploy script (plug-and-play).
      </p>
    </div>
  );
}
